import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  subject: string;
  customSubject?: string;
  message: string;
  acceptPrivacy: boolean;
  createdAtIso: string;
  processed: boolean;
  processingInfo?: string;
  status: string;
}

const AdminContacts: React.FC = () => {
  const [contacts, setContacts] = useState<ContactSubmission[]>([]);
  const [deletedContacts, setDeletedContacts] = useState<ContactSubmission[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [hoveredContact, setHoveredContact] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showDeletedTable, setShowDeletedTable] = useState(false);

  const statusOptions = [
    'Open',
    'In Process (Wilhelm)',
    'In Process (Antonia)', 
    'Done'
  ];

  useEffect(() => {
    fetchContacts();
  }, []);

  // Inject status selector into header
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      container.innerHTML = `
        <select
          id="statusSelect"
          value="${selectedStatus}"
          style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
        >
          <option value="all">All Status</option>
          ${statusOptions.map(status => 
            `<option value="${status}">${status}</option>`
          ).join('')}
        </select>
        <button
          type="button"
          onclick="window.adminContactsRefresh()"
          style="padding: 8px 16px; border: 1px solid #008F99; border-radius: 6px; font-size: 14px; background: white; color: #008F99; cursor: pointer; transition: all 0.2s ease; font-weight: 500;"
          onmouseover="this.style.background='#008F99'; this.style.color='white';"
          onmouseout="this.style.background='white'; this.style.color='#008F99';"
        >
          🔄 Refresh
        </button>
      `;

      // Add event listeners
      const selectElement = container.querySelector('select');
      if (selectElement) {
        selectElement.addEventListener('change', (e) => {
          setSelectedStatus((e.target as HTMLSelectElement).value);
        });
      }

      // Expose refresh function globally
      (window as any).adminContactsRefresh = fetchContacts;
    }

    return () => {
      // Cleanup
      if (container) {
        container.innerHTML = '';
      }
      delete (window as any).adminContactsRefresh;
    };
  }, [selectedStatus]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const client = generateClient<Schema>({ authMode: 'userPool' });

      // Fetch ALL contact submissions with pagination
      let allContacts: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.ContactSubmission.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allContacts = [...allContacts, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminContacts: Fetched ${response.data?.length || 0} contacts (total: ${allContacts.length})`);
      } while (nextToken);
      
      console.log(`✅ AdminContacts: Total contacts fetched: ${allContacts.length}`);

      // #region agent log
      // Log ALL contacts to find the Jan 1st submission showing Dec 17th
      allContacts.forEach((contact, index) => {
        if (contact && index < 10) {
          console.log(`Contact #${index}:`, {
            id: contact.id,
            name: contact.name,
            email: contact.email,
            createdAtIso: contact.createdAtIso
          });
        }
      });
      // #endregion

      // Separate active and deleted contacts based on status
      const activeContacts = allContacts.filter(contact => contact.status !== 'deleted');
      const deletedContacts = allContacts.filter(contact => contact.status === 'deleted');

      setContacts(activeContacts);
      setDeletedContacts(deletedContacts);
      setError(null);
    } catch (err) {
      console.error('Error fetching contact submissions:', err);
      setError('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.ContactSubmission.update({
        id: contactId,
        status: newStatus
      });
      
      // Update local state
      setContacts(prev => 
        prev.map(contact => 
          contact.id === contactId 
            ? { ...contact, status: newStatus }
            : contact
        )
      );
      
      setMessage({ type: 'success', text: `Contact status updated to ${newStatus}` });
      console.log(`✅ Contact status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating contact status:', err);
      setMessage({ type: 'error', text: 'Failed to update contact status' });
    }
  };

  const deleteContact = async (contact: ContactSubmission) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.ContactSubmission.update({
        id: contact.id,
        status: 'deleted'
      });
      
      // Move contact from active to deleted
      setContacts(prev => prev.filter(c => c.id !== contact.id));
      setDeletedContacts(prev => [...prev, { ...contact, status: 'deleted' }]);
      
      setMessage({ type: 'success', text: 'Contact deleted successfully' });
      console.log(`✅ Contact deleted: ${contact.name}`);
    } catch (err) {
      console.error('Error deleting contact:', err);
      setMessage({ type: 'error', text: 'Failed to delete contact' });
    }
  };

  const restoreContact = async (contact: ContactSubmission) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.ContactSubmission.update({
        id: contact.id,
        status: 'Open' // Restore to default status
      });
      
      // Move contact from deleted to active
      setDeletedContacts(prev => prev.filter(c => c.id !== contact.id));
      setContacts(prev => [...prev, { ...contact, status: 'Open' }]);
      
      setMessage({ type: 'success', text: 'Contact restored successfully' });
      console.log(`✅ Contact restored: ${contact.name}`);
    } catch (err) {
      console.error('Error restoring contact:', err);
      setMessage({ type: 'error', text: 'Failed to restore contact' });
    }
  };

  const filteredContacts = selectedStatus === 'all' 
    ? contacts 
    : contacts.filter(contact => contact.status === selectedStatus);

  // Sort by creation date based on sortOrder - handle null values safely
  const sortedContacts = filteredContacts.sort((a, b) => {
    const dateA = a?.createdAtIso ? new Date(a.createdAtIso).getTime() : 0;
    const dateB = b?.createdAtIso ? new Date(b.createdAtIso).getTime() : 0;
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  // Sort deleted contacts by the same order
  const sortedDeletedContacts = [...deletedContacts].sort((a, b) => {
    const dateA = a?.createdAtIso ? new Date(a.createdAtIso).getTime() : 0;
    const dateB = b?.createdAtIso ? new Date(b.createdAtIso).getTime() : 0;
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return { bg: '#fef3c7', color: '#92400e' };
      case 'In Process (Wilhelm)': return { bg: '#dbeafe', color: '#1e40af' };
      case 'In Process (Antonia)': return { bg: '#f3e8ff', color: '#7c3aed' };
      case 'Done': return { bg: '#d1fae5', color: '#065f46' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const getSubjectDisplay = (contact: ContactSubmission) => {
    const subjectMap: { [key: string]: string } = {
      'ki-kurse': 'KI-Kurse',
      'ki-impulse': 'KI-Impulse',
      'partnership': 'Partnerschaft/Kooperation',
      'individual': 'Individuelle Schulung',
      'general': 'Allgemeine Anfrage',
      'other': 'Sonstiges'
    };
    
    const displaySubject = subjectMap[contact.subject] || contact.subject;
    return contact.customSubject ? `${displaySubject} - ${contact.customSubject}` : displaySubject;
  };

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading contact submissions...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchContacts}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-card" style={{ paddingBottom: '60px' }}>
      {message && (
        <div style={{ padding: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}

      {sortedContacts.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📧</div>
          <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No contact submissions yet</h3>
          <p style={{ margin: 0 }}>
            {selectedStatus === 'all' 
              ? 'No contact submissions found. They will appear here when users submit the contact form.'
              : `No contact submissions with status "${selectedStatus}".`}
          </p>
          <div style={{ 
            marginTop: '24px', 
            padding: '16px', 
            background: '#f0f9ff', 
            borderRadius: '8px',
            border: '1px solid #bfdbfe'
          }}>
            <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
              💡 <strong>Contact system is ready!</strong> Contact form submissions will appear here 
              when users submit inquiries through the website contact form.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table className="admin-table" style={{ minWidth: '800px', width: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th 
                  onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                  style={{ 
                    cursor: 'pointer', 
                    userSelect: 'none',
                    width: '100px',
                    minWidth: '100px'
                  }}
                  title="Click to sort by date"
                >
                  Date {sortOrder === 'newest' ? '↓' : '↑'}
                </th>
                <th style={{ width: '140px', minWidth: '140px' }}>Name</th>
                <th style={{ width: '160px', minWidth: '160px' }}>Subject</th>
                <th style={{ width: '200px', minWidth: '180px' }}>Message</th>
                <th className="hide-on-mobile" style={{ width: '140px', minWidth: '120px' }}>Company</th>
                <th style={{ width: '180px', minWidth: '160px' }}>Contact Info</th>
                <th style={{ width: '120px', minWidth: '100px' }}>Status</th>
                <th style={{ width: '180px', minWidth: '160px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedContacts.map((contact) => {
                // Skip null/undefined contacts
                if (!contact) return null;
                
                const statusStyle = getStatusColor(contact.status || 'Open');
                
                return (
                  <tr 
                    key={contact.id}
                  >
                    <td
                      onMouseEnter={(e) => {
                        setHoveredContact(contact.id);
                        setHoverPosition({ x: e.clientX, y: e.clientY });
                      }}
                      onMouseLeave={() => setHoveredContact(null)}
                      onMouseMove={(e) => {
                        if (hoveredContact === contact.id) {
                          setHoverPosition({ x: e.clientX, y: e.clientY });
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                      }}
                    >
                      {contact.createdAtIso ? (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>
                            {new Date(contact.createdAtIso).toLocaleDateString('de-DE', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                          </div>
                          <div style={{ fontSize: '12px', color: '#374151' }}>
                            {new Date(contact.createdAtIso).toLocaleTimeString('de-DE', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#1f2937' }}>
                        {contact.name}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>
                        {getSubjectDisplay(contact)}
                      </div>
                    </td>
                    <td>
                      <div style={{ 
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {contact.message}
                      </div>
                    </td>
                    <td className="hide-on-mobile">{contact.company || '-'}</td>
                    <td>
                      <div style={{ minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉️ {contact.email}</div>
                        {contact.phone && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            📞 {contact.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color
                      }}>
                        {contact.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                        {statusOptions.map(status => {
                          if (status === contact.status) return null;
                          
                          return (
                            <button
                              key={status}
                              onClick={() => handleStatusChange(contact.id, status)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                              title={`Change status to ${status}`}
                            >
                              {status}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => deleteContact(contact)}
                          style={{
                            padding: '4px 8px',
                            border: '1px solid #dc2626',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#dc2626',
                            fontSize: '11px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#dc2626';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.color = '#dc2626';
                          }}
                          title="Delete contact"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Deleted Contacts Section */}
      {!loading && (
        <div className="admin-content-card" style={{ marginTop: '32px' }}>
          <div 
            onClick={() => setShowDeletedTable(!showDeletedTable)}
            style={{
              padding: '16px 24px',
              backgroundColor: '#f9fafb',
              borderBottom: showDeletedTable ? '1px solid #e5e7eb' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Deleted Contacts ({sortedDeletedContacts.length})
              </span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Click to {showDeletedTable ? 'hide' : 'show'}
              </span>
            </div>
            <span style={{ fontSize: '18px', color: '#6b7280', transform: showDeletedTable ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </div>
          
          {showDeletedTable && (
            <div style={{ overflowX: 'auto', width: '100%' }}>
              {sortedDeletedContacts.length === 0 ? (
                <div style={{ 
                  padding: '48px 24px', 
                  textAlign: 'center', 
                  color: '#6b7280',
                  backgroundColor: '#fefefe'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No deleted contacts</h3>
                  <p style={{ margin: 0 }}>
                    When you delete contacts, they will appear here and can be restored.
                  </p>
                </div>
              ) : (
                <table className="admin-table" style={{ minWidth: '800px', width: '100%', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <th 
                        onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                        style={{ 
                          cursor: 'pointer', 
                          userSelect: 'none',
                          width: '100px',
                          minWidth: '100px'
                        }}
                        title="Click to sort by date"
                      >
                        Date {sortOrder === 'newest' ? '↓' : '↑'}
                      </th>
                      <th style={{ width: '140px', minWidth: '140px' }}>Name</th>
                      <th style={{ width: '160px', minWidth: '160px' }}>Subject</th>
                      <th style={{ width: '200px', minWidth: '180px' }}>Message</th>
                      <th className="hide-on-mobile" style={{ width: '140px', minWidth: '120px' }}>Company</th>
                      <th style={{ width: '180px', minWidth: '160px' }}>Contact Info</th>
                      <th style={{ width: '120px', minWidth: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeletedContacts.map((contact) => (
                      <tr key={contact.id} style={{ backgroundColor: '#fefefe', opacity: 0.7 }}>
                        <td>
                          {contact.createdAtIso ? (
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}>
                                {new Date(contact.createdAtIso).toLocaleDateString('de-DE', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit'
                                })}
                              </div>
                              <div style={{ fontSize: '12px', color: '#374151' }}>
                                {new Date(contact.createdAtIso).toLocaleTimeString('de-DE', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </div>
                            </div>
                          ) : '-'}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>
                            {contact.name}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {getSubjectDisplay(contact)}
                          </div>
                        </td>
                        <td>
                          <div style={{ 
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {contact.message}
                          </div>
                        </td>
                        <td className="hide-on-mobile">{contact.company || '-'}</td>
                        <td>
                          <div style={{ minWidth: '150px' }}>
                            <div style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>✉️ {contact.email}</div>
                            {contact.phone && (
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                📞 {contact.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() => restoreContact(contact)}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #008F99',
                              borderRadius: '4px',
                              background: 'white',
                              color: '#008F99',
                              fontSize: '11px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#008F99';
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white';
                              e.currentTarget.style.color = '#008F99';
                            }}
                          >
                            Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Hover Popup */}
      {hoveredContact && (
        <div
          style={{
            position: 'fixed',
            left: Math.min(hoverPosition.x + 10, window.innerWidth - 420),
            top: hoverPosition.y > window.innerHeight / 2 
              ? Math.max(20, hoverPosition.y - 200)
              : hoverPosition.y + 10,
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            maxWidth: '400px',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          {(() => {
            const contact = contacts.find(c => c.id === hoveredContact);
            if (!contact) return null;
            return (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#008F99' }}>
                  📧 {getSubjectDisplay(contact)}
                </div>
                <div style={{ lineHeight: '1.4' }}>
                  💬 {contact.message}
                </div>
              </div>
            );
          })()}
        </div>
      )}
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminContacts;
